import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import requester, {
  UrlOptions,
} from '~/libs/api-hooks/architect/utils/requester';
import StatusError from '~/libs/api-hooks/architect/utils/statusError';
import { QueryMap } from '~/libs/api-hooks/architect/utils/prepareSearchParams';
import mapApiObjectToCamelCase, {
  ApiObject,
} from '~/libs/api-hooks/architect/utils/mapApiObjectToCamelCase';
import DeepPartial from '~/libs/api-hooks/architect/utils/DeepPartial';
import ExceptionLogger from '~/libs/api-hooks/architect/backplane/makeExceptionLogger';

export type ObjectListApiOptions<
  ListItemType,
  DeepPartialListItemType extends
    DeepPartial<ListItemType> = DeepPartial<ListItemType>,
> = Omit<
  UseQueryOptions<DeepPartialListItemType[], StatusError>,
  'queryKey' | 'queryFn'
>;

export type ObjectListApiResult<
  ListItemType,
  DeepPartialListItemType extends
    DeepPartial<ListItemType> = DeepPartial<ListItemType>,
> = UseQueryResult<DeepPartialListItemType[], StatusError>;

export interface UseObjectListApiProps<
  ListItemType,
  DeepPartialListItemType extends
    DeepPartial<ListItemType> = DeepPartial<ListItemType>,
> {
  pathname: string;
  cacheKey: string[];
  queryMap?: QueryMap;
  reactQueryOptions?: ObjectListApiOptions<
    ListItemType,
    DeepPartialListItemType
  >;
  urlOptions?: UrlOptions;
  includeApiKeyWhenAvailable?: boolean;
  customHeaders?: Record<string, string>;
}

const useObjectListApi = <
  ListItemType,
  DeepPartialListItemType extends
    DeepPartial<ListItemType> = DeepPartial<ListItemType>,
>({
  pathname,
  cacheKey,
  queryMap,
  reactQueryOptions = {},
  urlOptions,
  includeApiKeyWhenAvailable = true,
  customHeaders,
}: UseObjectListApiProps<
  ListItemType,
  DeepPartialListItemType
>): ObjectListApiResult<ListItemType, DeepPartialListItemType> => {
  const fetchAPI = async (): Promise<DeepPartialListItemType[]> => {
    const response = await requester({
      pathname,
      queryMap,
      method: 'GET',
      urlOptions,
      includeApiKeyWhenAvailable,
      sendCsrfToken: false,
      customHeaders,
    });
    try {
      const responseJson = await response.json();
      if (!Array.isArray(responseJson)) {
        throw new Error('ObjectListApi expects an array to be returned');
      }
      return responseJson.map((listItem: ApiObject) =>
        mapApiObjectToCamelCase<DeepPartialListItemType>(listItem),
      );
    } catch (error: any) {
      ExceptionLogger().error(error.message, { error });
      throw new StatusError({
        message: error.message,
        status: 0,
        pathname,
      });
    }
  };
  return useQuery<DeepPartialListItemType[], StatusError>(cacheKey, fetchAPI, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    ...reactQueryOptions,
  });
};

export default useObjectListApi;
